import {
  BACK_TO_SCHOOL_PREVIEW_CAMPAIGN_ID,
  BACK_TO_SCHOOL_PREVIEW_CLAIM_DEADLINE_MS,
  BACK_TO_SCHOOL_PREVIEW_PATH,
  isBackToSchoolPreviewCampaignClaimable,
} from "config/backToSchoolPreviewCampaign";
import { hasActiveSchoolLicense } from "./mailchimpTags";
import { isSubscriptionPaused } from "./subscriptionPauseStatus";
import { isTrialExpired, normalizeDateValue, resolveTrialEndDate } from "./trialStatus";

export const BACK_TO_SCHOOL_PREVIEW_PAGE_STATES = Object.freeze({
  LOADING: "loading",
  LOGGED_OUT: "logged_out",
  ELIGIBLE: "eligible",
  ACTIVE: "active",
  CLAIMED_EXPIRED: "claimed_expired",
  PAID: "paid",
  TRIAL_ACTIVE: "trial_active",
  SCHOOL_ACTIVE: "school_active",
  PAUSED: "paused",
  CAMPAIGN_ENDED: "campaign_ended",
  ERROR: "error",
});

export { BACK_TO_SCHOOL_PREVIEW_CAMPAIGN_ID, BACK_TO_SCHOOL_PREVIEW_PATH };

export function normalizePreviewDateValue(value) {
  return normalizeDateValue(value);
}

export function hasClaimedBackToSchoolPreview(user = {}) {
  return Boolean(user?.backToSchoolPreviewClaimedAt);
}

export function getBackToSchoolPreviewExpiresAt(user = {}) {
  return normalizePreviewDateValue(user?.backToSchoolPreviewExpiresAt);
}

export function isBackToSchoolPreviewActive(user = {}, options = {}) {
  if (!user?.uid) return false;
  if (user.backToSchoolPreviewCampaign !== BACK_TO_SCHOOL_PREVIEW_CAMPAIGN_ID) return false;
  if (!hasClaimedBackToSchoolPreview(user)) return false;
  if (isSubscriptionPaused(user, options)) return false;

  const expiresAt = getBackToSchoolPreviewExpiresAt(user);
  if (!expiresAt) return false;

  const nowDate = normalizePreviewDateValue(options.now) || new Date();
  return expiresAt.getTime() > nowDate.getTime();
}

export function isActiveNormalTrial(user = {}, options = {}) {
  return user?.trial === true && !isTrialExpired(user, options);
}

export function getBackToSchoolPreviewAccountStatus(user = {}, options = {}) {
  if (!user?.uid) return "logged_out";
  if (isSubscriptionPaused(user, options)) return "paused";
  if (user.premium === true) return "paid";
  if (hasActiveSchoolLicense(user)) return "school_active";
  if (isActiveNormalTrial(user, options)) return "trial_active";
  if (isBackToSchoolPreviewActive(user, options)) return "preview_active";
  if (hasClaimedBackToSchoolPreview(user)) return "preview_claimed";
  if (isTrialExpired(user, options)) return "expired_unpaid";
  return "inactive";
}

export function getBackToSchoolPreviewEligibility(user = {}, options = {}) {
  if (!user?.uid) {
    return { eligible: false, reason: "unauthenticated" };
  }
  if (!isBackToSchoolPreviewCampaignClaimable(options.now || new Date())) {
    return { eligible: false, reason: "campaign_ended" };
  }
  if (isSubscriptionPaused(user, options)) {
    return { eligible: false, reason: "not_eligible_paused" };
  }
  if (user.premium === true) {
    return { eligible: false, reason: "not_eligible_paid" };
  }
  if (hasActiveSchoolLicense(user)) {
    return { eligible: false, reason: "not_eligible_school" };
  }
  if (isActiveNormalTrial(user, options)) {
    return { eligible: false, reason: "not_eligible_trial_active" };
  }
  if (isBackToSchoolPreviewActive(user, options)) {
    return { eligible: false, reason: "preview_active" };
  }
  if (hasClaimedBackToSchoolPreview(user)) {
    return { eligible: false, reason: "already_claimed" };
  }
  if (!isTrialExpired(user, options)) {
    return { eligible: false, reason: "not_eligible_no_expired_trial" };
  }

  return { eligible: true, reason: "eligible" };
}

export function isEligibleForBackToSchoolPreview(user = {}, options = {}) {
  return getBackToSchoolPreviewEligibility(user, options).eligible;
}

export function getBackToSchoolPreviewPageState(user, options = {}) {
  if (options.loading) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.LOADING;
  if (options.error) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.ERROR;
  if (!user?.uid) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.LOGGED_OUT;
  if (isBackToSchoolPreviewActive(user, options)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.ACTIVE;
  if (hasClaimedBackToSchoolPreview(user)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.CLAIMED_EXPIRED;
  if (!isBackToSchoolPreviewCampaignClaimable(options.now || new Date())) {
    return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.CAMPAIGN_ENDED;
  }
  if (isSubscriptionPaused(user, options)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.PAUSED;
  if (user.premium === true) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.PAID;
  if (hasActiveSchoolLicense(user)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.SCHOOL_ACTIVE;
  if (isActiveNormalTrial(user, options)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.TRIAL_ACTIVE;
  if (isEligibleForBackToSchoolPreview(user, options)) return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.ELIGIBLE;
  return BACK_TO_SCHOOL_PREVIEW_PAGE_STATES.CAMPAIGN_ENDED;
}

export function getActiveTrialExpirationDate(user, options = {}) {
  if (!isActiveNormalTrial(user, options)) return null;
  return resolveTrialEndDate(user, options);
}

export function formatBackToSchoolPreviewExpiry(value, fallback = "the preview expiration date") {
  const date = normalizePreviewDateValue(value);
  if (!date) return fallback;

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

export function getBackToSchoolPreviewClaimDeadlineDate() {
  return new Date(BACK_TO_SCHOOL_PREVIEW_CLAIM_DEADLINE_MS);
}
